/**
 * One release in the MY RELEASES list.
 *
 * A row rather than a card, and for the reason SongCard's header gives: a list of
 * releases is a list, and the library next door already reads left to right. This is
 * deliberately the same shape — artwork, title, metadata, status, a More menu — so the
 * Publisher feels like the same product rather than a bolted-on area.
 *
 * §6 names the fields it must show: cover art, title, artist, type, release date,
 * status, outlet count, last updated, and actions. All nine are here.
 *
 * WHAT IT DOES NOT SHOW, and why that is not an omission: no store links, no play
 * counts, no "live on Spotify", no ISRC and no UPC. None of those facts exists for a
 * release that has never been delivered, and §23–§25 forbid inventing them. A release's
 * detail page shows the provider fields it actually has, which today is none.
 *
 * Same accessibility shape as SongCard: role="button" with a keyboard handler rather
 * than a real <button>, because it contains a button and nesting them is invalid HTML;
 * and the controls inside stop propagation so pressing Delete does not also open the
 * release.
 */
import { Trash2 } from 'lucide-react';
import { ActionMenu } from '../ui';
import ReleaseArtwork from './ReleaseArtwork';
import ReleaseStatusBadge from './ReleaseStatusBadge';
import {
  formatReleaseDate,
  formatTimestamp,
  outletCountLabel,
  trackCountLabel,
} from '../../lib/publisherFormat';
import { c } from '../../styles/theme';
import type { PublisherRelease, Song } from '../../types/domain';

export interface ReleaseRowProps {
  release: PublisherRelease;
  /** The library, so the row can resolve reused artwork and count real tracks. */
  songs: Song[];
  /** Optional provider-backed delivery summary for a released row. */
  deliverySummary?: string | null;
  onOpen: (releaseId: string) => void;
  onDelete: (release: PublisherRelease) => void;
}

export default function ReleaseRow({ release, songs, deliverySummary, onOpen, onDelete }: ReleaseRowProps) {
  /*
    The artist line falls back to the release type rather than to an em dash, because an
    untitled draft's row would otherwise be three placeholders in a column. "Untitled"
    is likewise a label for a state, not a value pretending to be a title.
  */
  const title = release.title.trim() || 'Untitled release';
  const artist = release.artistName.trim();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(release.id)}
      onKeyDown={(e) => {
        // Space activates a role="button" natively and would otherwise scroll.
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(release.id);
        }
      }}
      aria-label={`Open ${title}`}
      className="lyx-track-row lyx-release-row"
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 12,
        background: c.surface1,
        padding: 12,
        cursor: 'pointer',
      }}
    >
      <ReleaseArtwork
        release={release}
        songs={songs}
        height={52}
        radius={8}
        style={{ width: 52, flexShrink: 0, overflow: 'hidden' }}
      />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            margin: '0 0 3px',
          }}
        >
          <p
            style={{
              fontWeight: 500,
              fontSize: 13,
              margin: 0,
              color: release.title.trim() ? c.text1 : c.text2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </p>
          <ReleaseStatusBadge status={release.status} />
        </div>

        {/* Artist and type, then the dates and the outlet count. Two lines rather than
            one long dot-separated string: nine fields on one line at 390px is a
            paragraph, and the second line is the one a reader can skip. */}
        <p
          style={{
            fontSize: 11,
            color: c.text2,
            margin: '0 0 2px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {artist ? `${artist} · ` : ''}
          {release.releaseType} · {trackCountLabel(release.songIds.length)}
        </p>
        <p style={{ fontSize: 11, color: c.text2, margin: 0 }}>
          {/* "Release date" and "Updated" are labelled rather than left as two bare
              dates: on a draft both are unset and two em dashes side by side say
              nothing. §26 — neither is invented when absent. A delivered-outlet summary,
              when present, is still phrased as delivery reporting rather than as a claim
              that the release is live in any specific store. */}
          Release date {formatReleaseDate(release.releaseDate)} ·{' '}
          {deliverySummary ?? `${outletCountLabel(release.outletKeys.length)} selected`} · Updated{' '}
          {formatTimestamp(release.updatedAt)}
        </p>
      </div>

      <div
        className="lyx-row-controls"
        style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/*
          Delete is the only action in the menu, and it is in a menu rather than beside
          the row because it is destructive. There is deliberately no "Retry", "Take
          down" or "View on Spotify" here: each of those is an operation against a
          release that exists at the distributor, and none of these do. The detail page
          offers what is genuinely available per release.
        */}
        <ActionMenu
          label={`More actions for ${title}`}
          stopPropagation
          triggerStyle={{ width: 30, height: 30 }}
          items={[
            {
              label: 'Delete release',
              ariaLabel: `Delete ${title}`,
              icon: <Trash2 size={13} color={c.danger} />,
              onSelect: () => onDelete(release),
              danger: true,
            },
          ]}
        />
      </div>
    </div>
  );
}
